import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { ReactNode } from 'react'

export interface Column<T> {
  header: string
  field: keyof T | string
  render?: (row: T) => ReactNode
  align?: 'right' | 'left' | 'center'
  width?: string | number
}

interface TransactionTableProps<T> {
  title: string
  columns: Column<T>[]
  data: T[]
  onRowClick?: (row: T) => void
}

const TransactionTable = <T,>({ title, columns, data, onRowClick }: TransactionTableProps<T>) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Typography variant="h6" sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {title}
      </Typography>
      <TableContainer 
        sx={{ 
          maxWidth: '100%',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)',
            },
          },
        }}
      >
        <Table size={isMobile ? 'small' : 'small'} sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.header} align={column.align} sx={{ width: column.width }}>
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              <TableRow
                key={idx}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <TableCell key={column.header} align={column.align}>
                    {column.render ? (
                      column.render(row)
                    ) : column.field === 'status' ? (
                      <Chip
                        size="small"
                        label={(row as any)[column.field]}
                        color={
                          (row as any)[column.field] === 'COMPLETED'
                            ? 'success'
                            : (row as any)[column.field] === 'FAILED'
                              ? 'error'
                              : 'warning'
                        }
                        variant="outlined"
                      />
                    ) : (
                      ((row as any)[column.field as keyof T] as ReactNode)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default TransactionTable

